import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  Users,
  UserCircle2,
  ClipboardList,
  Wallet,
  Sparkles,
  LogOut,
  Save,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Check,
  Pencil,
  Calendar,
  Clock,
  Trophy,
  Shirt,
  Target,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import { useSettings } from "../context/SettingsContext";
import { useSeason } from "../context/SeasonContext";
import { useAuth } from "../context/AuthContext";
import { to12Hour } from "../utils/time";
import { toTitleCase } from "../utils/titleCase";
import {
  saveDraft,
  loadDraft,
  clearDraft,
  isFormDataBlank,
} from "../utils/registrationDraft";
import PlayerAutocomplete from "../components/registration/PlayerAutocomplete";
import ReturningCaptainLogin from "../components/registration/ReturningCaptainLogin";
import ImportPreviousTeamPanel from "../components/registration/ImportPreviousTeamPanel";
import EarlyBirdCountdown from "../components/registration/EarlyBirdCountdown";
import { parseLocalDate } from "../utils/date";
import { getTeamStyle } from "../utils/teamColors";

const GLOBAL_ANIMS = `
  @keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
  .animate-fade-in { animation: fadeIn 0.3s cubic-bezier(0.23, 1, 0.32, 1); }
`;

const STEPS = [
  { key: "team", label: "Team" },
  { key: "captain", label: "Captain" },
  { key: "roster", label: "Roster" },
  { key: "payment", label: "Payment" },
  { key: "review", label: "Review" },
];

const StepIndicator = ({ step, teamStyle }) => {
  const foundIdx = STEPS.findIndex(
    (s) => s.key === (step === "review" ? "review" : step),
  );
  const currentIdx = foundIdx === -1 ? 0 : foundIdx;
  return (
    <div className="flex items-center justify-between mb-8 relative">
      <div className="absolute top-3 left-0 right-0 h-[3px] bg-gray-200 dark:bg-gray-800 rounded" />
      <motion.div
        className="absolute top-3 h-[3px] rounded"
        style={{
          background:
            teamStyle?.accentStyle?.background ||
            teamStyle?.headerStyle?.background ||
            "#f97316",
        }}
        initial={{ width: "20%" }}
        animate={{ width: `${((currentIdx + 1) / STEPS.length) * 100}%` }}
        transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
      />
      {STEPS.map((s, idx) => {
        const isDone = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <div key={s.key} className="flex flex-col items-center relative z-10">
            <motion.div
              className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all duration-200 border-2 ${
                isDone
                  ? "bg-emerald-500 border-emerald-500 text-white"
                  : isCurrent
                    ? "border-white dark:border-gray-900 bg-white dark:bg-gray-900 shadow-lg shadow-orange-500/30 text-orange-600 dark:text-orange-400"
                    : "border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-400"
              }`}
              whileHover={{ scale: 1.1 }}
            >
              {isDone ? <Check size={14} /> : idx + 1}
            </motion.div>
            <span
              className={`text-[10px] mt-1.5 font-medium tracking-widest uppercase transition-colors ${
                isCurrent
                  ? "text-orange-600 dark:text-orange-400"
                  : "text-gray-400 dark:text-gray-500"
              }`}
            >
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, accentColor }) => (
  <div className="flex items-center gap-3 mb-5">
    <div
      className="flex h-9 w-9 items-center justify-center rounded-2xl"
      style={{ backgroundColor: accentColor ? `#${accentColor}20` : "#f3f4f6" }}
    >
      <Icon
        size={20}
        style={{ color: accentColor ? `#${accentColor}` : "#f97316" }}
      />
    </div>
    <h2 className="text-2xl font-semibold tracking-tight dark:text-white">
      {title}
    </h2>
  </div>
);

const blankPlayer = () => ({
  name: "",
  age: "",
  isMuslim: true,
  jerseyNumber: "",
  jerseySize: "L",
  sourcePlayerId: null,
});

const isPlayerFilled = (p) => !!p.name.trim() && !!p.age && !!p.jerseySize;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ConfirmModal = ({ isOpen, onConfirm, onCancel, title, message }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full mx-4 overflow-hidden shadow-2xl"
          >
            <div className="p-8">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="text-amber-500" size={28} />
                <h3 className="text-2xl font-semibold dark:text-white">
                  {title}
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {message}
              </p>
            </div>
            <div className="border-t dark:border-gray-800 flex">
              <button
                onClick={onCancel}
                className="flex-1 py-4 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="flex-1 py-4 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors"
              >
                Reduce Players
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const Register = () => {
  const {
    settings,
    registrationSettings,
    isEarlyBird,
    isRegistrationOpen,
    getCurrentPrice,
  } = useSettings();
  const { registrationSeason } = useSeason();
  const { user, signOut } = useAuth();

  // Use registrationSettings for the registration page, fall back to settings
  const activeSettings = registrationSettings || settings;

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [playerCountInput, setPlayerCountInput] = useState("");
  const [formData, setFormData] = useState(null);
  const [step, setStep] = useState("team");
  const [earlyBirdExpired, setEarlyBirdExpired] = useState(false);

  const handleEarlyBirdExpire = () => {
    setEarlyBirdExpired(true);
    setTimeout(() => setEarlyBirdExpired(false), 6000);
  };

  // Import feature state
  const [importSource, setImportSource] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [selections, setSelections] = useState({
    teamName: false,
    logo: false,
    colors: false,
  });

  // Existing-captain email check
  const [emailCheck, setEmailCheck] = useState({
    checking: false,
    hasAccount: false,
    checkedEmail: null,
  });
  const [loginTrigger, setLoginTrigger] = useState(0);

  // Validation
  const [fieldErrors, setFieldErrors] = useState({ players: {} });
  const teamNameRef = useRef(null);
  const captainNameRef = useRef(null);
  const captainEmailRef = useRef(null);
  const rosterErrorRef = useRef(null);
  const playerRefs = useRef([]);

  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingPlayerCount, setPendingPlayerCount] = useState(null);

  // Team style
  const [teamAccent, setTeamAccent] = useState(null);

  useEffect(() => {
    if (!activeSettings) return;
    setFormData({
      teamName: "",
      captainName: "",
      captainEmail: "",
      captainPhone: "",
      numPlayers: activeSettings.min_players,
      refFeeCoverage: false,
      logoUrl: null,
      primaryColor: null,
      secondaryColor: null,
      gradient: false,
      players: Array(activeSettings.min_players).fill(null).map(blankPlayer),
    });
  }, [activeSettings]);

  const [captainInfoPrefilled, setCaptainInfoPrefilled] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [draftBanner, setDraftBanner] = useState(null);
  const [draftCleared, setDraftCleared] = useState(false);
  const [manualToggle, setManualToggle] = useState({});

  useEffect(() => {
    if (formData) setPlayerCountInput(String(formData.numPlayers));
  }, [formData, formData?.numPlayers]);

  // Prefill captain
  useEffect(() => {
    if (
      !user ||
      user.role !== "team_captain" ||
      !formData ||
      captainInfoPrefilled
    )
      return;
    setFormData((prev) => ({
      ...prev,
      captainName: prev.captainName || user.name || "",
      captainEmail: user.email || "",
    }));
    setCaptainInfoPrefilled(true);
  }, [user, formData, captainInfoPrefilled]);

  // Draft handling
  useEffect(() => {
    if (!formData || !registrationSeason?.id || draftRestored) return;
    const draft = loadDraft(registrationSeason.id);
    if (draft && !isFormDataBlank(draft.formData)) {
      setDraftBanner(draft);
    }
    setDraftRestored(true);
  }, [formData, registrationSeason, draftRestored]);

  useEffect(() => {
    if (!formData || !registrationSeason?.id || !draftRestored || draftBanner)
      return;
    if (isFormDataBlank(formData)) return;
    const timeout = setTimeout(() => {
      saveDraft(registrationSeason.id, formData);
    }, 800);
    return () => clearTimeout(timeout);
  }, [formData, registrationSeason, draftRestored, draftBanner]);

  // Fetch previous team
  useEffect(() => {
    const fetchPreviousTeam = async () => {
      if (!user || user.role !== "team_captain") return;
      setImportLoading(true);
      try {
        const { data: team, error: teamErr } = await supabase
          .from("teams")
          .select("*")
          .eq("captain_id", user.id)
          .order("season_id", { ascending: false })
          .order("id", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (teamErr || !team) return;

        const { data: players } = await supabase
          .from("players")
          .select("*")
          .eq("team_id", team.id);

        setImportSource({ team, players: players || [] });
      } finally {
        setImportLoading(false);
      }
    };

    fetchPreviousTeam();
  }, [user]);

  useEffect(() => {
    if (!user && emailCheck.hasAccount) {
      setLoginTrigger((t) => t + 1);
    }
  }, [emailCheck.hasAccount, user]);

  // Dynamic team styling
  useEffect(() => {
    if (!formData) return;
    const styleData = {
      primary_color: formData.primaryColor,
      secondary_color: formData.secondaryColor,
      gradient: formData.gradient,
    };
    const style = getTeamStyle(styleData);
    setTeamAccent(style);
  }, [formData]);

  if (!activeSettings || !formData) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-8">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-80 bg-gray-100 dark:bg-gray-800 rounded-3xl animate-pulse"
              />
            ))}
          </div>
          <div className="lg:col-span-4">
            <div className="sticky top-8 h-96 bg-gray-100 dark:bg-gray-800 rounded-3xl animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!registrationSeason) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-6">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl p-12 text-center border border-gray-100 dark:border-gray-800">
          <h1 className="text-4xl font-bold mb-4 dark:text-white">
            Registration Not Open
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Check back soon for the next season!
          </p>
        </div>
      </div>
    );
  }

  const basePrice = getCurrentPrice();
  const additionalPlayers = Math.max(
    0,
    formData.numPlayers - activeSettings.base_player_count,
  );
  const additionalPlayerFee =
    additionalPlayers * activeSettings.additional_player_fee;
  const refFee = formData.refFeeCoverage ? activeSettings.ref_fee_coverage : 0;
  const totalCost = basePrice + additionalPlayerFee + refFee;
  const filledPlayerCount = formData.players.filter(isPlayerFilled).length;

  const updatePlayerCount = (count) => {
    if (Number.isNaN(count)) return;

    const newCount = Math.max(
      activeSettings.min_players,
      Math.min(activeSettings.max_players, count),
    );

    if (newCount < formData.players.length) {
      const removed = formData.players.slice(newCount);
      const hasData = removed.some(
        (p) =>
          p.name.trim() || p.age || p.jerseyNumber !== "" || p.sourcePlayerId,
      );

      if (hasData) {
        setPendingPlayerCount(newCount);
        setShowConfirmModal(true);
        return;
      }
    }

    applyPlayerCountChange(newCount);
  };

  const applyPlayerCountChange = (newCount) => {
    const newPlayers = [...formData.players];
    if (newCount > formData.players.length) {
      for (let i = formData.players.length; i < newCount; i++) {
        newPlayers.push(blankPlayer());
      }
    } else {
      newPlayers.splice(newCount);
    }
    setFormData({ ...formData, numPlayers: newCount, players: newPlayers });
    setShowConfirmModal(false);
    setPendingPlayerCount(null);
  };

  const updatePlayer = (index, field, value) => {
    const newPlayers = [...formData.players];
    newPlayers[index] = { ...newPlayers[index], [field]: value };
    setFormData({ ...formData, players: newPlayers });
  };

  // Import handlers
  const handleToggleTeamName = () => {
    const next = !selections.teamName;
    setSelections({ ...selections, teamName: next });
    setFormData({
      ...formData,
      teamName: next ? toTitleCase(importSource.team.name) : "",
    });
  };

  const handleToggleLogo = () => {
    const next = !selections.logo;
    setSelections({ ...selections, logo: next });
    setFormData({
      ...formData,
      logoUrl: next ? importSource.team.logo_url : null,
    });
  };

  const handleToggleColors = () => {
    const next = !selections.colors;
    setSelections({ ...selections, colors: next });
    setFormData({
      ...formData,
      primaryColor: next ? importSource.team.primary_color : null,
      secondaryColor: next ? importSource.team.secondary_color : null,
      gradient: next ? !!importSource.team.gradient : false,
    });
  };

  const importedPlayerIds = new Set(
    formData.players
      .filter((p) => p.sourcePlayerId)
      .map((p) => p.sourcePlayerId),
  );

  const handleTogglePlayer = (importedPlayer) => {
    const alreadyImported = importedPlayerIds.has(importedPlayer.id);

    if (alreadyImported) {
      const newPlayers = formData.players.map((p) =>
        p.sourcePlayerId === importedPlayer.id ? blankPlayer() : p,
      );
      setFormData({ ...formData, players: newPlayers });
      return;
    }

    const newPlayers = [...formData.players];
    let targetIndex = newPlayers.findIndex(
      (p) => !p.name.trim() && !p.sourcePlayerId,
    );

    const filled = {
      name: importedPlayer.name || "",
      age: importedPlayer.age != null ? String(importedPlayer.age) : "",
      isMuslim: importedPlayer.is_muslim ?? true,
      jerseyNumber:
        importedPlayer.jersey_number != null
          ? String(importedPlayer.jersey_number)
          : "",
      jerseySize: importedPlayer.jersey_size || "L",
      sourcePlayerId: importedPlayer.id,
    };

    let newNumPlayers = formData.numPlayers;
    if (targetIndex === -1) {
      if (newPlayers.length >= activeSettings.max_players) return;
      newPlayers.push(filled);
      targetIndex = newPlayers.length - 1;
      newNumPlayers = newPlayers.length;
    } else {
      newPlayers[targetIndex] = filled;
    }

    setFormData({
      ...formData,
      players: newPlayers,
      numPlayers: newNumPlayers,
    });
    setManualToggle((prev) => ({ ...prev, [targetIndex]: false }));
  };

  const handleCaptainEmailBlur = async () => {
    if (user) return;
    const email = formData.captainEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    if (emailCheck.checkedEmail === email) return;

    setEmailCheck({ checking: true, hasAccount: false, checkedEmail: email });
    try {
      const { data, error } = await supabase.functions.invoke(
        "check-captain-email",
        {
          body: { email },
        },
      );
      if (error) throw error;
      setEmailCheck({
        checking: false,
        hasAccount: !!data?.hasAccount,
        checkedEmail: email,
      });
    } catch (err) {
      setEmailCheck({
        checking: false,
        hasAccount: false,
        checkedEmail: email,
      });
    }
  };

  const handleRestoreDraft = () => {
    const restored = { ...draftBanner.formData };
    if (user?.role === "team_captain") {
      restored.captainEmail = user.email || restored.captainEmail;
    }
    setFormData(restored);
    setDraftBanner(null);

    const collapsed = {};
    restored.players.forEach((p, i) => {
      if (isPlayerFilled(p)) collapsed[i] = false;
    });
    setManualToggle(collapsed);
  };

  const handleDiscardDraft = () => {
    clearDraft(registrationSeason.id);
    setDraftBanner(null);
  };

  const handleClearSavedDraft = () => {
    clearDraft(registrationSeason.id);
    setDraftCleared(true);
    setTimeout(() => setDraftCleared(false), 2500);
  };

  const isPlayerExpanded = (index) =>
    manualToggle[index] === undefined ? true : manualToggle[index];

  const togglePlayerExpanded = (index) => {
    setManualToggle((prev) => ({
      ...prev,
      [index]: !isPlayerExpanded(index),
    }));
  };

  const collapseAllFilled = () => {
    const next = {};
    formData.players.forEach((p, i) => {
      if (isPlayerFilled(p)) next[i] = false;
    });
    setManualToggle((prev) => ({ ...prev, ...next }));
  };

  const expandAll = () => {
    const next = {};
    formData.players.forEach((_, i) => (next[i] = true));
    setManualToggle((prev) => ({ ...prev, ...next }));
  };

  // Validation
  const validateForm = () => {
    const errors = { players: {} };
    let firstErrorEl = null;

    if (!formData.teamName.trim()) {
      errors.teamName = "Team name is required.";
      firstErrorEl = firstErrorEl || teamNameRef.current;
    }

    if (!formData.captainName.trim()) {
      errors.captainName = "Captain name is required.";
      firstErrorEl = firstErrorEl || captainNameRef.current;
    }

    if (!formData.captainEmail.trim()) {
      errors.captainEmail = "Captain email is required.";
      firstErrorEl = firstErrorEl || captainEmailRef.current;
    } else if (!EMAIL_RE.test(formData.captainEmail.trim())) {
      errors.captainEmail = "Enter a valid email address.";
      firstErrorEl = firstErrorEl || captainEmailRef.current;
    }

    const nonMuslimCount = formData.players.filter((p) => !p.isMuslim).length;
    if (nonMuslimCount > activeSettings.max_non_muslim_players) {
      errors.roster = `Maximum ${activeSettings.max_non_muslim_players} non-Muslim players allowed.`;
      firstErrorEl = firstErrorEl || rosterErrorRef.current;
    }

    const jerseyNumbers = formData.players
      .map((p) => p.jerseyNumber)
      .filter((n) => n !== "");
    const dupSet = new Set(
      jerseyNumbers.filter((num, idx) => jerseyNumbers.indexOf(num) !== idx),
    );

    formData.players.forEach((p, idx) => {
      const playerErr = {};
      if (!p.name.trim()) playerErr.name = "Name required";
      if (!p.age || parseInt(p.age) < activeSettings.min_age) {
        playerErr.age = `Must be ${activeSettings.min_age}+`;
      }
      if (
        p.jerseyNumber !== "" &&
        (!Number.isInteger(parseInt(p.jerseyNumber)) ||
          p.jerseyNumber < 0 ||
          p.jerseyNumber > 99)
      ) {
        playerErr.jerseyNumber = "0-99 only";
      } else if (p.jerseyNumber !== "" && dupSet.has(p.jerseyNumber)) {
        playerErr.jerseyNumber = "Duplicate";
      }
      if (Object.keys(playerErr).length > 0) {
        errors.players[idx] = playerErr;
        if (!firstErrorEl) firstErrorEl = playerRefs.current[idx];
      }
    });

    setFieldErrors(errors);

    if (Object.keys(errors.players).length > 0) {
      setManualToggle((prev) => {
        const next = { ...prev };
        Object.keys(errors.players).forEach((idx) => (next[idx] = true));
        return next;
      });
    }

    const hasErrors =
      errors.teamName ||
      errors.captainName ||
      errors.captainEmail ||
      errors.roster ||
      Object.keys(errors.players).length > 0;

    if (hasErrors && firstErrorEl) {
      setTimeout(() => {
        firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
      return false;
    }

    return true;
  };

  const handleContinueToReview = () => {
    if (!validateForm()) return;
    setStep("review");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitRegistration = async () => {
    setLoading(true);
    setSubmitError(null);
    try {
      const { data, error } = await supabase.functions.invoke("register-team", {
        body: {
          seasonId: registrationSeason.id,
          teamName: formData.teamName,
          captainName: formData.captainName,
          captainEmail: formData.captainEmail,
          captainPhone: formData.captainPhone,
          refFeeCoverage: formData.refFeeCoverage,
          importedFromTeamId: importSource?.team?.id ?? null,
          importLogo: selections.logo,
          importColors: selections.colors,
          players: formData.players.map((p) => ({
            name: p.name,
            age: parseInt(p.age),
            isMuslim: p.isMuslim,
            jerseyNumber: p.jerseyNumber || null,
            jerseySize: p.jerseySize,
          })),
        },
      });

      if (error) throw error;

      clearDraft(registrationSeason.id);
      navigate("/registration-success", {
        state: { teamName: formData.teamName, totalCost: data.total_cost },
      });
    } catch (err) {
      setSubmitError(
        err.message || "An error occurred while submitting. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isRegistrationOpen()) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-6">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl p-12 text-center border border-red-100 dark:border-red-900/30">
          <h1 className="text-4xl font-bold mb-4 text-red-600">
            Registration Closed
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            The deadline has passed. See you next season.
          </p>
        </div>
      </div>
    );
  }

  const inputCls = (hasError) =>
    `w-full px-5 py-3.5 border-2 rounded-2xl dark:bg-gray-950 text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
      hasError
        ? "border-red-400 focus:ring-red-400"
        : "border-gray-200 dark:border-gray-800 focus:border-orange-500 focus:ring-orange-500"
    }`;

  const accentColor =
    teamAccent?.accentHex ||
    teamAccent?.primaryHex?.replace("#", "") ||
    "f97316";

  // REVIEW STEP
  if (step === "review") {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <style>{GLOBAL_ANIMS}</style>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-6 sm:p-10 border border-gray-100 dark:border-gray-800"
        >
          <StepIndicator step={step} teamStyle={teamAccent} />

          <div className="max-w-2xl mx-auto">
            <div className="flex justify-center mb-8">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="inline-flex items-center gap-2 px-6 py-2 bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 rounded-2xl text-sm font-semibold"
              >
                <CheckCircle2 size={18} /> Almost there — review and submit
              </motion.div>
            </div>

            <h1 className="text-center text-3xl sm:text-5xl font-bold tracking-tighter dark:text-white mb-2">
              Review Registration
            </h1>
            <p className="text-center text-gray-500 dark:text-gray-400 text-base sm:text-lg mb-8 sm:mb-12">
              {registrationSeason?.name}
            </p>

            <div className="space-y-8">
              {/* Team */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gray-50 dark:bg-gray-950/80 rounded-3xl p-8"
              >
                <div className="flex items-center gap-4 mb-6">
                  {formData.logoUrl && (
                    <img
                      src={formData.logoUrl}
                      alt="logo"
                      className="h-16 w-16 rounded-2xl object-cover ring-1 ring-gray-200 dark:ring-gray-700"
                    />
                  )}
                  <div>
                    <div className="font-semibold text-3xl dark:text-white">
                      {formData.teamName}
                    </div>
                    {(formData.primaryColor || formData.secondaryColor) && (
                      <div className="flex gap-2 mt-2">
                        {formData.primaryColor && (
                          <div
                            className="w-6 h-6 rounded-full ring-2 ring-white dark:ring-gray-900"
                            style={{
                              backgroundColor: `#${formData.primaryColor}`,
                            }}
                          />
                        )}
                        {formData.secondaryColor && (
                          <div
                            className="w-6 h-6 rounded-full ring-2 ring-white dark:ring-gray-900"
                            style={{
                              backgroundColor: `#${formData.secondaryColor}`,
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Captain */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gray-50 dark:bg-gray-950/80 rounded-3xl p-8"
              >
                <SectionHeader
                  icon={UserCircle2}
                  title="Captain"
                  accentColor={accentColor}
                />
                <div className="flex items-center gap-4 text-lg">
                  <div className="font-medium dark:text-white">
                    {formData.captainName}
                  </div>
                  <div className="text-gray-400">•</div>
                  <div className="text-gray-400">{formData.captainEmail}</div>
                  {formData.captainPhone && (
                    <div className="text-gray-400">
                      • {formData.captainPhone}
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Roster */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gray-50 dark:bg-gray-950/80 rounded-3xl p-8"
              >
                <SectionHeader
                  icon={Users}
                  title={`Roster • ${formData.players.length} players`}
                  accentColor={accentColor}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {formData.players.map((p, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 * i }}
                      className="flex items-center gap-3 bg-white dark:bg-gray-900 rounded-2xl px-5 py-4 border border-gray-100 dark:border-gray-800"
                    >
                      <div className="font-mono text-sm text-gray-400 w-5">
                        #{i + 1}
                      </div>
                      <div className="flex-1 truncate font-medium dark:text-white">
                        {p.name}
                      </div>
                      {p.jerseyNumber && (
                        <div className="font-mono text-xs px-2.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">
                          #{p.jerseyNumber}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        {p.age} • {p.jerseySize}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Cost */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/40 rounded-3xl p-8 border border-orange-100 dark:border-orange-900"
              >
                <SectionHeader
                  icon={Wallet}
                  title="Payment Summary"
                  accentColor={accentColor}
                />
                <div className="space-y-4 text-lg">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Base Registration
                    </span>
                    <span className="font-semibold text-orange-600 dark:text-orange-400">
                      ${basePrice}
                    </span>
                  </div>
                  {additionalPlayers > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Additional Players ({additionalPlayers})
                      </span>
                      <span className="font-semibold text-orange-600 dark:text-orange-400">
                        +${additionalPlayerFee}
                      </span>
                    </div>
                  )}
                  {refFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Referee Coverage
                      </span>
                      <span className="font-semibold text-orange-600 dark:text-orange-400">
                        +${refFee}
                      </span>
                    </div>
                  )}
                  <div className="h-px bg-orange-200 dark:bg-orange-900 my-2" />
                  <div className="flex justify-between text-3xl font-bold text-orange-600">
                    <span>Total</span>
                    <span>${totalCost}</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {submitError && (
              <div className="mt-8 flex items-start gap-3 rounded-2xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-5 text-red-700 dark:text-red-400">
                <AlertTriangle size={20} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">
                    Couldn't submit your registration
                  </div>
                  <div className="text-sm mt-0.5">{submitError}</div>
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 mt-8 sm:mt-12">
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.985 }}
                onClick={() => setStep("team")}
                className="flex-1 py-4 sm:py-5 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-700 rounded-2xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-950 transition-all"
              >
                Edit Details
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.985 }}
                onClick={submitRegistration}
                disabled={loading}
                className="flex-1 py-4 sm:py-5 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-2xl font-bold text-base sm:text-lg hover:brightness-110 active:scale-[0.985] transition-all flex items-center justify-center gap-2 sm:gap-3 disabled:opacity-70"
              >
                {loading ? "Submitting..." : `Confirm & Submit • $${totalCost}`}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // FORM LAYOUT
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <style>{GLOBAL_ANIMS}</style>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        {/* LEFT COLUMN - FORM */}
        <div className="lg:col-span-8">
          <StepIndicator step={step} teamStyle={teamAccent} />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl p-6 sm:p-10 border border-gray-100 dark:border-gray-800"
          >
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter mb-2 dark:text-white">
              {registrationSeason.name} Registration
            </h1>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
              Complete your team entry below
            </p>

            {/* Draft Banner */}
            <AnimatePresence>
              {draftBanner && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-8 border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/60 rounded-2xl p-6"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <Save className="text-blue-500 shrink-0" />
                      <div>
                        <div className="font-medium text-blue-700 dark:text-blue-300">
                          Draft saved
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(draftBanner.savedAt).toLocaleDateString()}{" "}
                          at{" "}
                          {new Date(draftBanner.savedAt).toLocaleTimeString(
                            [],
                            {
                              hour: "numeric",
                              minute: "2-digit",
                            },
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleRestoreDraft}
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-2xl font-medium hover:bg-blue-700 transition"
                      >
                        Restore
                      </motion.button>
                      <button
                        onClick={handleDiscardDraft}
                        className="px-5 py-2.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition"
                      >
                        Discard
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Early Bird / Pricing Alert */}
            <AnimatePresence mode="wait">
              {isEarlyBird() ? (
                <motion.div
                  key="early-bird"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-8 flex items-start gap-4 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-6"
                >
                  <Sparkles className="text-emerald-600 mt-0.5" size={26} />
                  <div>
                    <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                      Early Bird Pricing Active
                    </div>
                    <div className="text-sm text-emerald-600 dark:text-emerald-400 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>
                        Save $
                        {Math.max(
                          0,
                          (activeSettings.regular_price || 0) -
                            (activeSettings.early_bird_price || 0),
                        )}
                      </span>
                      <span className="text-emerald-400 dark:text-emerald-600">
                        •
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <EarlyBirdCountdown
                          deadline={activeSettings.early_bird_deadline}
                          onExpire={handleEarlyBirdExpire}
                          className="text-emerald-700 dark:text-emerald-300 font-semibold"
                        />
                        left
                      </span>
                      <span className="hidden sm:inline text-emerald-400 dark:text-emerald-600">
                        •
                      </span>
                      <span className="hidden sm:inline text-emerald-600/80 dark:text-emerald-400/80">
                        Ends{" "}
                        {new Intl.DateTimeFormat("en-US", {
                          dateStyle: "medium",
                        }).format(
                          parseLocalDate(activeSettings.early_bird_deadline),
                        )}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="regular-price"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-2xl p-6 text-amber-700 dark:text-amber-300"
                >
                  {earlyBirdExpired
                    ? "Early bird pricing just ended — regular pricing now applies."
                    : "Regular pricing now active."}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Season Highlights */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {[
                {
                  icon: Calendar,
                  label: "Season Start",
                  value: new Intl.DateTimeFormat("en-US", {
                    dateStyle: "medium",
                  }).format(parseLocalDate(activeSettings.season_start_date)),
                },
                {
                  icon: Clock,
                  label: "Game Times",
                  value: `${to12Hour(activeSettings.game_time_slot_1?.slice(0, 5))} & ${to12Hour(activeSettings.game_time_slot_2?.slice(0, 5))} & ${to12Hour(activeSettings.game_time_slot_3?.slice(0, 5))}`,
                },
                {
                  icon: Users,
                  label: "Players",
                  value: `${activeSettings.base_player_count} base`,
                },
                {
                  icon: Shirt,
                  label: "Jerseys",
                  value: activeSettings.rules?.jerseys_provided
                    ? "Provided"
                    : "Player-supplied",
                },
                {
                  icon: Trophy,
                  label: "Playoffs",
                  value: "Play-in Guaranteed",
                },
                {
                  icon: Target,
                  label: "Non-Muslims",
                  value: `Max ${activeSettings.max_non_muslim_players}`,
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -2 }}
                  className="bg-gray-50 dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-2xl p-5 flex gap-4 items-start"
                >
                  <item.icon
                    className="text-orange-500 mt-0.5 shrink-0"
                    size={22}
                  />
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest text-gray-400">
                      {item.label}
                    </div>
                    <div className="font-medium dark:text-white mt-0.5 text-sm sm:text-base break-words">
                      {item.value}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {user?.role === "team_captain" ? (
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-50 dark:bg-gray-950 rounded-2xl px-6 py-4 text-sm">
                <div className="flex items-center gap-3">
                  <UserCircle2 className="text-emerald-500 shrink-0" />
                  <span className="truncate">
                    Logged in as{" "}
                    <span className="font-medium">{user.email}</span>
                  </span>
                </div>
                <button
                  onClick={signOut}
                  className="flex items-center gap-2 text-red-500 hover:text-red-600 self-start sm:self-auto"
                >
                  <LogOut size={16} /> Sign out
                </button>
              </div>
            ) : (
              <div className="mt-8">
                <ReturningCaptainLogin
                  openTrigger={loginTrigger}
                  prefillEmail={formData.captainEmail}
                />
              </div>
            )}

            {user?.role === "team_captain" && (
              <ImportPreviousTeamPanel
                importSource={importSource}
                loading={importLoading}
                selections={selections}
                onToggleTeamName={handleToggleTeamName}
                onToggleLogo={handleToggleLogo}
                onToggleColors={handleToggleColors}
                importedPlayerIds={importedPlayerIds}
                onTogglePlayer={handleTogglePlayer}
              />
            )}

            {/* TEAM INFORMATION */}
            <div className="mt-14">
              <SectionHeader
                icon={ClipboardList}
                title="Team Information"
                accentColor={accentColor}
              />
              <div>
                <label className="block text-sm font-semibold mb-2 dark:text-white">
                  TEAM NAME
                </label>
                <input
                  ref={teamNameRef}
                  type="text"
                  value={formData.teamName}
                  onChange={(e) =>
                    setFormData({ ...formData, teamName: e.target.value })
                  }
                  onBlur={(e) =>
                    setFormData({
                      ...formData,
                      teamName: toTitleCase(e.target.value),
                    })
                  }
                  className={
                    inputCls(!!fieldErrors.teamName) +
                    " text-gray-900 dark:text-orange-500"
                  }
                  placeholder="Enter your team name"
                />
                {fieldErrors.teamName && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-1.5">
                    <AlertTriangle size={14} />
                    {fieldErrors.teamName}
                  </p>
                )}
              </div>
            </div>

            {/* CAPTAIN */}
            <div className="mt-16">
              <SectionHeader
                icon={UserCircle2}
                title="Team Captain"
                accentColor={accentColor}
              />
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 dark:text-white">
                    FULL NAME
                  </label>
                  <input
                    ref={captainNameRef}
                    type="text"
                    value={formData.captainName}
                    onChange={(e) =>
                      setFormData({ ...formData, captainName: e.target.value })
                    }
                    className={
                      inputCls(!!fieldErrors.captainName) +
                      " text-gray-900 dark:text-orange-500"
                    }
                  />
                  {fieldErrors.captainName && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-1.5">
                      <AlertTriangle size={14} />
                      {fieldErrors.captainName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 dark:text-white">
                    EMAIL ADDRESS
                  </label>
                  <input
                    ref={captainEmailRef}
                    type="email"
                    value={formData.captainEmail}
                    onChange={(e) =>
                      setFormData({ ...formData, captainEmail: e.target.value })
                    }
                    onBlur={handleCaptainEmailBlur}
                    disabled={user?.role === "team_captain"}
                    className={`${inputCls(!!fieldErrors.captainEmail)} ${user?.role === "team_captain" ? "opacity-75" : ""} text-gray-900 dark:text-orange-500`}
                  />
                  {fieldErrors.captainEmail && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-1.5">
                      <AlertTriangle size={14} />
                      {fieldErrors.captainEmail}
                    </p>
                  )}
                  {!fieldErrors.captainEmail && emailCheck.checking && (
                    <p className="text-gray-400 text-xs mt-2">
                      Checking email…
                    </p>
                  )}
                  {!fieldErrors.captainEmail &&
                    !emailCheck.checking &&
                    emailCheck.hasAccount &&
                    !user && (
                      <p className="text-blue-600 dark:text-blue-400 text-xs mt-2 flex items-center gap-1.5">
                        <UserCircle2 size={14} />
                        Looks like you already have an account — log in above to
                        auto-fill your team.
                      </p>
                    )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-2 dark:text-white">
                    PHONE NUMBER (OPTIONAL)
                  </label>
                  <input
                    type="tel"
                    value={formData.captainPhone}
                    onChange={(e) =>
                      setFormData({ ...formData, captainPhone: e.target.value })
                    }
                    className={
                      inputCls(false) + " text-gray-900 dark:text-orange-500"
                    }
                    placeholder="(216) 123-4567"
                  />
                </div>
              </div>
            </div>

            {/* ROSTER */}
            <div className="mt-16">
              <div className="mb-6">
                <SectionHeader
                  icon={Users}
                  title="Roster"
                  accentColor={accentColor}
                />
                <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-sm mt-4">
                  <button
                    onClick={collapseAllFilled}
                    className="text-blue-600 hover:underline whitespace-nowrap"
                  >
                    Collapse filled
                  </button>
                  <button
                    onClick={expandAll}
                    className="text-blue-600 hover:underline whitespace-nowrap"
                  >
                    Expand all
                  </button>
                  <div className="flex items-center gap-2 pl-0 sm:pl-4 sm:border-l">
                    <span className="font-medium dark:text-white whitespace-nowrap">
                      Players
                    </span>
                    <input
                      type="number"
                      value={playerCountInput}
                      onChange={(e) => setPlayerCountInput(e.target.value)}
                      onBlur={() => {
                        const parsed = parseInt(playerCountInput, 10);
                        if (Number.isNaN(parsed)) {
                          setPlayerCountInput(String(formData.numPlayers)); // revert
                          return;
                        }
                        updatePlayerCount(parsed);
                      }}
                      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                      className="w-16 sm:w-20 text-center px-2 sm:px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-2xl focus:outline-none focus:ring-1"
                      min={activeSettings.min_players}
                      max={activeSettings.max_players}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6 flex justify-between text-xs text-gray-500">
                <div>
                  {filledPlayerCount} / {formData.players.length} complete
                </div>
                {filledPlayerCount === formData.players.length && (
                  <div className="text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 size={14} />
                    Roster ready
                  </div>
                )}
              </div>

              <div className="h-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden mb-10">
                <motion.div
                  className="h-1 bg-gradient-to-r from-orange-500 to-amber-500"
                  initial={{ width: 0 }}
                  animate={{
                    width: `${(filledPlayerCount / formData.players.length) * 100}%`,
                  }}
                  transition={{ duration: 0.4 }}
                />
              </div>

              <div ref={rosterErrorRef}>
                {fieldErrors.roster && (
                  <div className="mb-8 p-5 border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800 rounded-2xl text-red-700 dark:text-red-400 flex gap-3">
                    <AlertTriangle size={22} /> {fieldErrors.roster}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <AnimatePresence>
                  {formData.players.map((player, index) => {
                    const err = fieldErrors.players[index] || {};
                    const hasErr = Object.keys(err).length > 0;
                    const filled = isPlayerFilled(player);
                    const expanded = isPlayerExpanded(index);
                    const imported = !!player.sourcePlayerId;
                    const playerAccent = imported
                      ? "#10b981"
                      : filled
                        ? "#f59e0b"
                        : "#6b7280";

                    if (filled && !expanded) {
                      return (
                        <motion.div
                          key={`collapsed-${index}`}
                          ref={(el) => (playerRefs.current[index] = el)}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          onClick={() => togglePlayerExpanded(index)}
                          className="group flex items-center justify-between
                          border border-gray-200 dark:border-gray-800
                          hover:border-orange-200 dark:hover:border-orange-800
                          bg-white dark:bg-gray-900 rounded-3xl px-4 sm:px-7 py-5
                          cursor-pointer transition-all duration-200"
                          style={{
                            borderLeftWidth: 4,
                            borderLeftColor: hasErr ? "#ef4444" : playerAccent,
                          }}
                        >
                          <div className="flex items-center gap-3 sm:gap-5 min-w-0 flex-1">
                            <div className="font-mono text-lg sm:text-xl text-gray-300 dark:text-gray-700 shrink-0">
                              {index + 1}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold dark:text-white text-base sm:text-lg truncate">
                                {player.name}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500">
                                Age {player.age} • {player.jerseySize} • #
                                {player.jerseyNumber || "—"}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                            {imported && (
                              <span className="hidden sm:inline text-xs px-3 py-1 bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 rounded-full">
                                Imported
                              </span>
                            )}
                            <Pencil
                              size={18}
                              className="text-gray-400 group-hover:text-orange-500 transition"
                            />
                          </div>
                        </motion.div>
                      );
                    }

                    return (
                      <motion.div
                        key={`expanded-${index}`}
                        ref={(el) => (playerRefs.current[index] = el)}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className={`border-2 rounded-3xl p-7 transition-all 
                          duration-200 ${
                            hasErr
                              ? "border-red-300"
                              : filled
                                ? "border-emerald-200 dark:border-emerald-800"
                                : "border-gray-200 dark:border-gray-800"
                          }`}
                        style={{
                          borderLeftWidth: 4,
                          borderLeftColor: hasErr ? "#ef4444" : playerAccent,
                        }}
                      >
                        <div className="flex justify-between items-center mb-6">
                          <div className="flex items-center gap-3">
                            <div className="font-semibold text-xl dark:text-white">
                              Player {index + 1}
                            </div>
                            {imported && (
                              <div className="px-3 py-1 text-xs bg-emerald-100 dark:bg-emerald-900 rounded-full text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                                <Check size={14} />
                                Imported
                              </div>
                            )}
                            {filled && !hasErr && (
                              <div className="text-emerald-600 text-xs font-medium flex items-center gap-1">
                                <CheckCircle2 size={15} />
                                Ready
                              </div>
                            )}
                          </div>
                          {filled && (
                            <button
                              onClick={() => togglePlayerExpanded(index)}
                              className="text-xs uppercase tracking-widest text-blue-600 hover:text-blue-700"
                            >
                              Collapse
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                          <div className="md:col-span-2">
                            <label className="block text-xs tracking-widest font-medium mb-2 text-gray-500 dark:text-gray-400">
                              PLAYER NAME
                            </label>
                            <PlayerAutocomplete
                              value={player.name}
                              onChange={(name) =>
                                updatePlayer(index, "name", name)
                              }
                              onAutofill={({
                                name,
                                age,
                                jerseySize,
                                isMuslim,
                              }) => {
                                const newPlayers = [...formData.players];
                                newPlayers[index] = {
                                  ...newPlayers[index],
                                  name,
                                  age: String(age),
                                  jerseySize,
                                  isMuslim,
                                };
                                setFormData({
                                  ...formData,
                                  players: newPlayers,
                                });
                              }}
                              placeholder="Full name"
                              className={
                                inputCls(!!err.name) +
                                " text-gray-900 dark:text-orange-500"
                              }
                            />
                            {err.name && (
                              <p className="text-red-500 text-xs mt-2">
                                {err.name}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs tracking-widest font-medium mb-2 text-gray-500 dark:text-gray-400">
                              AGE
                            </label>
                            <input
                              type="number"
                              min={activeSettings.min_age}
                              value={player.age}
                              onChange={(e) =>
                                updatePlayer(index, "age", e.target.value)
                              }
                              className={
                                inputCls(!!err.age) +
                                " text-gray-900 dark:text-orange-500"
                              }
                              placeholder={`${activeSettings.min_age}+`}
                            />
                            {err.age && (
                              <p className="text-red-500 text-xs mt-2">
                                {err.age}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs tracking-widest font-medium mb-2 text-gray-500 dark:text-gray-400">
                              JERSEY #
                            </label>
                            <input
                              type="number"
                              value={player.jerseyNumber}
                              min={0}
                              max={99}
                              onChange={(e) =>
                                updatePlayer(
                                  index,
                                  "jerseyNumber",
                                  e.target.value,
                                )
                              }
                              className={
                                inputCls(!!err.jerseyNumber) +
                                " text-gray-900 dark:text-orange-500"
                              }
                              placeholder="0-99"
                            />
                            {err.jerseyNumber && (
                              <p className="text-red-500 text-xs mt-2">
                                {err.jerseyNumber}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs tracking-widest font-medium mb-2 text-gray-500 dark:text-gray-400">
                              SIZE
                            </label>
                            <select
                              value={player.jerseySize}
                              onChange={(e) =>
                                updatePlayer(
                                  index,
                                  "jerseySize",
                                  e.target.value,
                                )
                              }
                              className="w-full px-5 py-3.5 border-2 text-gray-900 dark:text-orange-500 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                              {["S", "M", "L", "XL", "2XL", "3XL"].map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="mt-6 flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={player.isMuslim}
                            onChange={(e) =>
                              updatePlayer(index, "isMuslim", e.target.checked)
                            }
                            className="w-5 h-5 accent-orange-600"
                          />
                          <span className="dark:text-white">Muslim player</span>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </div>

            {/* PAYMENT SECTION */}
            <div className="mt-16 border-t pt-12">
              <SectionHeader
                icon={Wallet}
                title="Cost Breakdown"
                accentColor={accentColor}
              />
              <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 border border-gray-100 dark:border-gray-800 rounded-3xl p-9">
                <div className="space-y-6">
                  <div className="flex justify-between items-baseline">
                    <span className="text-lg text-gray-900 dark:text-gray-500">
                      Base Registration
                    </span>
                    <span className="text-2xl font-semibold text-gray-900 dark:text-gray-500">
                      ${basePrice}
                    </span>
                  </div>
                  {additionalPlayers > 0 && (
                    <div className="flex justify-between items-baseline">
                      <span className="text-lg text-gray-900 dark:text-gray-500">
                        Additional Players × {additionalPlayers}
                      </span>
                      <span className="text-2xl font-semibold text-gray-900 dark:text-gray-500">
                        +${additionalPlayerFee}
                      </span>
                    </div>
                  )}
                  {refFee > 0 && (
                    <div className="flex justify-between items-baseline">
                      <span className="text-lg text-gray-900 dark:text-gray-500">
                        Referee Coverage
                      </span>
                      <span className="text-2xl font-semibold">+${refFee}</span>
                    </div>
                  )}
                  <div className="pt-6 border-t flex justify-between text-4xl font-bold text-orange-600 dark:text-orange-400">
                    <span>Total Due</span>
                    <span>${totalCost}</span>
                  </div>
                </div>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.985 }}
              onClick={handleContinueToReview}
              className="mt-10 w-full py-5 sm:py-7 bg-gradient-to-r from-orange-600 via-amber-600 to-orange-600 text-white font-bold text-lg sm:text-2xl rounded-3xl flex items-center justify-center gap-2 sm:gap-4 active:scale-[0.985] transition-all shadow-xl shadow-orange-500/30"
            >
              <span className="truncate">Review &amp; Submit Registration</span>
              <ChevronRight size={24} className="sm:w-8 sm:h-8 shrink-0" />
            </motion.button>

            <p className="text-center mt-8 text-xs text-gray-400 flex flex-wrap items-center justify-center gap-2">
              <span>Progress autosaves</span>
              <span>•</span>
              <button
                onClick={handleClearSavedDraft}
                className="underline hover:text-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={12} /> Clear draft
              </button>
              {draftCleared && (
                <span className="text-emerald-500">✓ Cleared</span>
              )}
            </p>
          </motion.div>
        </div>

        {/* RIGHT COLUMN - STICKY SIDEBAR */}
        <div className="lg:col-span-4">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="sticky top-8"
          >
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl p-6 sm:p-8 border border-gray-100 dark:border-gray-800">
              <div className="uppercase tracking-[2px] text-xs font-mono mb-4 text-orange-500">
                Live Summary
              </div>

              <div className="mb-8">
                <div className="text-4xl sm:text-6xl font-bold text-orange-600 tabular-nums break-all">
                  ${totalCost}
                </div>
                <div className="text-xs sm:text-sm text-gray-500 mt-1">
                  USD due upon confirmation
                </div>
              </div>

              <div className="space-y-6 text-sm">
                <div>
                  <div className="font-medium mb-3 flex items-center gap-2 text-green-700 dark:text-green-400">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    REGISTRATION DETAILS
                  </div>
                  <div className="space-y-3 text-gray-600 dark:text-gray-400">
                    <div className="flex justify-between">
                      <span>Base players</span>
                      <span>{activeSettings.base_player_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Additional</span>
                      <span>{additionalPlayers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Non-Muslims allowed</span>
                      <span>{activeSettings.max_non_muslim_players}</span>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t dark:border-gray-700">
                  <div className="text-xs uppercase tracking-widest text-gray-400 mb-4">
                    Payment methods
                  </div>
                  <div className="flex flex-wrap gap-2 sm:gap-4">
                    <div className="px-4 sm:px-5 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 text-xs font-medium rounded-2xl">
                      ZELLE
                    </div>
                    <div className="px-4 sm:px-5 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 text-xs font-medium rounded-2xl">
                      VENMO
                    </div>
                    <div className="px-4 sm:px-5 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 text-xs font-medium rounded-2xl">
                      CASH APP
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-[10px] text-center text-gray-400">
              All data is saved automatically to your browser
            </div>
          </motion.div>
        </div>
      </div>

      {/* CONFIRM MODAL */}
      <ConfirmModal
        isOpen={showConfirmModal}
        title="Reduce roster size?"
        message={`You are about to remove the last ${formData.players.length - pendingPlayerCount} player entries. Any data entered will be lost. Continue?`}
        onConfirm={() => applyPlayerCountChange(pendingPlayerCount)}
        onCancel={() => {
          setShowConfirmModal(false);
          setPendingPlayerCount(null);
        }}
      />

      {/* FIXED BOTTOM BAR */}
      <div className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 hidden lg:block max-w-[calc(100vw-2rem)]">
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-gray-900 text-white text-sm px-6 sm:px-8 py-3 sm:py-4 rounded-3xl flex items-center gap-4 sm:gap-8 shadow-2xl shadow-black/70"
        >
          <div className="flex items-center gap-2 whitespace-nowrap">
            <Wallet className="text-orange-400" size={18} /> Current total
          </div>
          <div className="text-2xl sm:text-3xl font-semibold text-orange-400 tabular-nums">
            ${totalCost}
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleContinueToReview}
            className="bg-white text-gray-900 px-6 sm:px-8 py-2.5 sm:py-3 rounded-2xl font-semibold hover:bg-orange-50 transition whitespace-nowrap text-sm sm:text-base"
          >
            Review &amp; Submit
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
